/**
 * Hot seat: a whole game in one browser, with no server behind it.
 *
 * The engine is the same one the server runs, so this is not a simulation of
 * the game — it is the game, with the browser holding the state instead of a
 * room. That makes it the honest end-to-end test of the client (Slice 2.10),
 * and it is how you play across a table with the laptop in the middle.
 *
 * The seat on show follows whoever must decide, so a player only ever sees
 * their own cards: the view is rebuilt for that seat, exactly as `redact.ts`
 * would build it for a connection. Every accepted command is kept, because the
 * seed plus the commands rebuild the game — which is what lets a test compare
 * what the screen showed against a fresh engine run of the same moves.
 */
import {
  apply,
  decisionMaker,
  newGame,
  viewFor,
  type Command,
  type GameEvent,
  type GameState,
  type PlayerCount,
  type PlayerId,
  type View,
} from '@trajan/engine';

export interface Move {
  actor: PlayerId;
  command: Command;
}

export interface HotSeat {
  seed: string;
  playerCount: PlayerCount;
  state: GameState;
  /** Every event, in order: the same log a room keeps. */
  log: GameEvent[];
  /** Every accepted command, in order. The seed and these rebuild the game. */
  moves: Move[];
  /** The last refusal, kept so the screen can say what the engine objected to. */
  error: string | null;
}

export function startHotSeat(seed: string, playerCount: PlayerCount): HotSeat {
  return {
    seed,
    playerCount,
    state: newGame({ seed, playerCount }),
    log: [],
    moves: [],
    error: null,
  };
}

/**
 * The seat the person at the keyboard is playing. Between decisions — at the
 * very end, above all — nobody is owed a turn, so the last seat to act keeps
 * the screen rather than it blanking or jumping to seat 0.
 */
export function seatToPlay(hs: HotSeat): PlayerId {
  return decisionMaker(hs.state) ?? hs.moves[hs.moves.length - 1]?.actor ?? 0;
}

/** What that seat sees — their own cards, and nobody else's. */
export function hotSeatView(hs: HotSeat): View {
  return viewFor(hs.state, seatToPlay(hs));
}

/**
 * Play a command for whoever is to decide. A refusal is kept as text and
 * changes nothing else: the engine is the authority here as much as it is in a
 * room, and the client never second-guesses it.
 */
export function playHotSeat(hs: HotSeat, command: Command): HotSeat {
  const actor = decisionMaker(hs.state);
  if (actor === null) return { ...hs, error: 'the game is not waiting for anyone' };
  const r = apply(hs.state, command, actor);
  if (!r.ok) return { ...hs, error: `${r.error.code}: ${r.error.message}` };
  return {
    ...hs,
    state: r.state,
    log: [...hs.log, ...r.events],
    moves: [...hs.moves, { actor, command }],
    error: null,
  };
}

/**
 * The same game again from its seed and its moves. A room rebuilds itself this
 * way after a restart; here it is what proves the screen and the engine agree,
 * by replaying what was played and comparing the result.
 */
export function replayHotSeat(seed: string, playerCount: PlayerCount, moves: Move[]): GameState {
  let state = newGame({ seed, playerCount });
  for (const [i, m] of moves.entries()) {
    const r = apply(state, m.command, m.actor);
    if (!r.ok) throw new Error(`replay failed at move ${i}: ${r.error.message}`);
    state = r.state;
  }
  return state;
}
